"use client";

import { clsx } from "clsx";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";

import type { OrbTextureName } from "@ui/components/core/Orb/Orb";
import { PlayPauseButton } from "@ui/components/core/PlayPauseButton";

const Orb = dynamic(() => import("@ui/components/core/Orb/Orb").then(mod => mod.Orb), {
  ssr: false,
});

const CREATIVE_TEXTURES: OrbTextureName[] = [
  "creative-1",
  "creative-2",
  "creative-3",
  "creative-4",
  "creative-5",
  "creative-6",
  "creative-7",
  "creative-8",
  "creative-9",
];

function getTrackTexture(index: number): OrbTextureName {
  return CREATIVE_TEXTURES[index % CREATIVE_TEXTURES.length];
}

// Visible corner radius for the orb thumbnail. The wrapper clips with overflow-hidden,
// so this value must match the cornerRadius prop on <Orb> for the shader to render
// the correct geometry under the clip.
const ORB_RADIUS_PX = 6;
const ORB_RADIUS_CLASS = "tw-rounded-[6px]";

interface AudioRowProps {
  title: string;
  index: number;
  isPlaying: boolean;
  analyser: AnalyserNode | null;
  onToggle: () => void;
}

export function AudioRow({ title, index, isPlaying, analyser, onToggle }: AudioRowProps) {
  const t = useTranslations("ClientKeys.AudioPlayerBar");

  return (
    <button
      type='button'
      onClick={onToggle}
      aria-pressed={isPlaying}
      aria-label={isPlaying ? `${t("pause")} ${title}` : `${t("play")} ${title}`}
      className='tw-group tw-flex tw-w-full tw-items-center tw-gap-2 tw-bg-transparent tw-outline-none focus-visible:tw-outline-focus tw-rounded-sm tw-cursor-pointer'
    >
      <span
        aria-hidden='true'
        className={clsx(
          "tw-relative tw-flex tw-size-7 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-transition-transform tw-duration-200 tw-ease-out",
          ORB_RADIUS_CLASS,
          isPlaying && "tw-scale-110",
        )}
      >
        <Orb
          texture={getTrackTexture(index)}
          animated={isPlaying}
          outputAnalyser={isPlaying ? analyser : undefined}
          cornerRadius={ORB_RADIUS_PX}
          image={{ sizes: "28px" }}
        />
        <span
          className={clsx(
            "tw-pointer-events-none tw-absolute tw-inset-0 tw-border-0.5 tw-border-black/15",
            ORB_RADIUS_CLASS,
          )}
        />
        <span
          className={clsx(
            "tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-transition-opacity tw-duration-150",
            isPlaying ? "tw-opacity-100" : "tw-opacity-0 group-hover:tw-opacity-100 group-focus-visible:tw-opacity-100",
          )}
        >
          <PlayPauseButton
            isPlaying={isPlaying}
            variant='ghost'
            iconSize='sm'
            className='tw-text-white tw-drop-shadow'
          />
        </span>
      </span>
      <span className='tw-block tw-truncate tw-type-xs-tight tw-py-1.5 tw-text-left tw-text-black tw-min-w-0'>
        {title}
      </span>
    </button>
  );
}
